import React, { Suspense, useEffect, useContext } from 'react'
import { Route, Routes, Navigate, useNavigate, useParams } from 'react-router-dom'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { resolveKioskConfig } from 'src/clinic/services/screenConfigMigration'

const KioskLayout = React.lazy(() => import('./KioskLayout'))
const KioskSelectFlowPage = React.lazy(() => import('src/clinic/components/KioskSelectFlowPage'))

// TEMPORARY COMPAT (docs/SCREEN_PORTAL_MIGRATION.md): the OOH/token screen moved
// to the Display Portal at /display/:location/:mentor. Live devices that still
// open the old /kiosk/display/:location/:mentor URL (bookmark / cached PWA /
// saved config) are redirected there so they keep working. Remove once all
// devices have reloaded onto /display.
function LegacyKioskDisplayRedirect() {
  const { location, mentor } = useParams()
  return <Navigate to={`/display/${location}/${mentor}`} replace />
}

// Component to handle base /kiosk redirect logic
// This replaces the redirect logic that was previously in ClinicLandingPage and CALandingPageModern
function KioskLandingRedirect() {
  const navigate = useNavigate()
  const { assistantData } = useContext(ClinicDataContext)

  useEffect(() => {
    let cancelled = false

    function readLegacyLocalConfig() {
      try {
        const kioskConfigStr = localStorage.getItem('kiosk-config')
        const roleCaConfigStr = localStorage.getItem('ROLE_CA_CONFIG')
        return kioskConfigStr
          ? JSON.parse(kioskConfigStr)
          : roleCaConfigStr
          ? JSON.parse(roleCaConfigStr)
          : {}
      } catch {
        return {}
      }
    }

    async function resolve() {
      // Server is the source of truth for what this kiosk shows.
      // resolveKioskConfig() handles: server config → one-time migration of any
      // legacy localStorage config to the server → write-through cache fallback
      // when offline. Kiosk Portal = video-call (chamber) screens only.
      const cfg = await resolveKioskConfig()
      if (cancelled) return
      if (cfg?.screenDeviceConfigId && cfg?.virtualChamberRoomId) {
        navigate('/kiosk/chamber-screen-home', { replace: true })
        return
      }
      // TEMPORARY COMPAT: a legacy kiosk device still configured for the
      // appointment/token (OOH) screen belongs to the Display Portal now —
      // cross-navigate it there. (The server never returns OOH fields for a
      // kiosk user, so this is purely a local-config bridge.)
      const legacyConfig = readLegacyLocalConfig()
      if (legacyConfig?.LOCATION_FOR_OOH && legacyConfig?.MENTOR_FOR_OOH) {
        const location = assistantData?.associatedClinicsData?.find(
          (l) => l?.id === legacyConfig?.LOCATION_FOR_OOH,
        )
        const locationSlug = location?.slug || legacyConfig?.LOCATION_FOR_OOH
        navigate(`/display/${locationSlug}/${legacyConfig?.MENTOR_FOR_OOH}`, { replace: true })
        return
      }
      // Nothing configured yet.
      navigate('/kiosk/select-flow', { replace: true })
    }

    resolve()
    return () => {
      cancelled = true
    }
  }, [navigate, assistantData])

  return <LoadingSpinner />
}

export default function KioskRoutesHandler() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Kiosk Portal = video-call (chamber) screens + their config. The OOH
            appointment/token screen + its config now live in the Display Portal
            (/display/*). */}
        <Route path="chamber-screen-home/*" element={<KioskLayout />} />
        <Route path="select-flow" element={<KioskSelectFlowPage />} />

        {/* TEMPORARY COMPAT: legacy OOH URL → Display Portal. */}
        <Route path="display/:location/:mentor" element={<LegacyKioskDisplayRedirect />} />

        {/* Base /kiosk route - handles redirect logic based on config */}
        <Route path="/" element={<KioskLandingRedirect />} />
        
        {/* Catch-all - redirect to select-flow */}
        <Route path="*" element={<Navigate to="/kiosk/select-flow" replace />} />
      </Routes>
    </Suspense>
  )
}

